import { Vector3 } from 'three';
import { Color } from '@thive/canvas';
import { useCallback } from 'react';

import { logger } from '@common/logging/logger';
import { FDDM_TAG } from '@/modules/floorplanDesign/constants';
import { useReferenceImageUIStore } from '@/modules/referenceImage/store';
import {
  buildScaleCrossElements as buildReferenceScaleCrossElements,
  buildScaleLineElement as buildReferenceScaleLineElement,
} from '@/modules/referenceImage/helpers';
import {
  REFERENCE_SCALING_ELEMENTS_COLOR_HEX,
  REFERENCE_SCALE_ELEMENT_STROKE_WIDTH,
  REFERENCE_POINT_A_ELEMENT_ID,
  REFERENCE_POINT_B_ELEMENT_ID,
} from '@/modules/referenceImage/constants';

import { Z_INDEX_MAP } from '../../constants/elements';
import { useCanvasStore } from '../../store';

export const useReferenceScaleClick = () => {
  const firstClick = useCallback((point: { x: number; y: number }) => {
    const canvas = useCanvasStore.getState().instance;
    if (!canvas) return;

    const { referenceImageActions } = useReferenceImageUIStore.getState();

    referenceImageActions.setScaleDraft((current) => {
      if (!current) {
        logger.error('Expected scaleDraft to be defined in updater');
        return current;
      }

      return {
        ...current,
        pointA: {
          x: point.x,
          y: point.y,
        },
      };
    });

    // Render first point
    const startCross = buildReferenceScaleCrossElements(
      REFERENCE_POINT_A_ELEMENT_ID,
      canvas.getZoom(),
      point,
    );
    canvas.updateElements(startCross);
    canvas.updateTool('Draw.LineSticky', {
      type: 'Line',
      pointA: new Vector3(point.x, point.y, Z_INDEX_MAP[FDDM_TAG.REFERENCE_SCALING_LINE]),
      stroke: Color.fromHex(REFERENCE_SCALING_ELEMENTS_COLOR_HEX),
      strokeWidth: REFERENCE_SCALE_ELEMENT_STROKE_WIDTH,
      autoStrokeWidth: true,
    });
  }, []);

  const secondClick = useCallback((point: { x: number; y: number }) => {
    const { referenceImageActions } = useReferenceImageUIStore.getState();

    // Update scale draft state
    referenceImageActions.setScaleDraft((current) => {
      if (!current) {
        logger.error('Expected scaleDraft to be defined in updater');
        return current;
      }

      return {
        ...current,
        pointB: {
          x: point.x,
          y: point.y,
        },
      };
    });

    const canvas = useCanvasStore.getState().instance;
    if (!canvas) return;

    const { scaleDraft } = useReferenceImageUIStore.getState().referenceImage;
    if (!scaleDraft || !scaleDraft.pointA || !scaleDraft.pointB) {
      logger.error('Expected scaling points to be defined.');
      return;
    }

    // Render the second point and the connecting line
    const endCross = buildReferenceScaleCrossElements(
      REFERENCE_POINT_B_ELEMENT_ID,
      canvas.getZoom(),
      point,
    );
    const line = buildReferenceScaleLineElement(scaleDraft.pointA, scaleDraft.pointB);
    canvas.updateElements([...endCross, line]);

    canvas.activateTool('Select');
  }, []);

  const scaleClick = useCallback(
    (point: { x: number; y: number }) => {
      const { scaleDraft } = useReferenceImageUIStore.getState().referenceImage;

      if (!scaleDraft) {
        logger.error('Expected scaleDraft to be defined');
        return;
      }

      if (!scaleDraft.pointA) {
        firstClick(point);
      } else if (!scaleDraft.pointB) {
        secondClick(point);
      }
    },
    [firstClick, secondClick],
  );

  return { scaleClick };
};
